import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import {
  CheckCircle2,
  PhoneCall,
  Sparkles,
  GraduationCap,
  LayoutDashboard,
  Globe2,
  BadgeCheck,
  ArrowRight,
  LifeBuoy,
} from 'lucide-react';

const steps = [
  {
    icon: PhoneCall,
    title: 'Screening Call',
    desc:
      'Our team will connect with you within 48–72 hours to understand your background and intent.',
  },
  {
    icon: Sparkles,
    title: 'Expertise Alignment',
    desc:
      'We\u2019ll evaluate your strengths, areas of expertise, and alignment with the Soulversity experience.',
  },
  {
    icon: GraduationCap,
    title: 'Training with Master Dev OM',
    desc:
      'A guided onboarding and training experience designed to align experts with Soulversity\u2019s methodology and standards.',
  },
];

const unlocks = [
  { icon: LayoutDashboard, label: 'Access to your expert dashboard' },
  { icon: Globe2, label: 'Ability to offer services globally' },
  { icon: BadgeCheck, label: 'Official onboarding as a Soulversity expert' },
];

const ExpertApplicationSubmitted: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-background overflow-x-hidden">
      <Navbar />

      <main className="flex-1 w-full">
        <div className="w-full mx-auto flex flex-col items-center px-4 sm:px-6 py-12 sm:py-20">

          {/* Success visual */}
          <div className="relative h-28 w-28 sm:h-32 sm:w-32 animate-fade-in">
            <div className="absolute inset-0 rounded-full bg-ifind-teal/15 blur-2xl animate-breathe" />
            <div className="relative h-full w-full rounded-full bg-card border border-border flex items-center justify-center">
              <CheckCircle2
                className="!h-14 !w-14 sm:!h-16 sm:!w-16 text-ifind-teal"
                strokeWidth={1.75}
              />
            </div>
          </div>

          {/* Heading block */}
          <div className="mt-8 flex flex-col items-center text-center max-w-2xl mx-auto animate-fade-in">
            <h1 className="font-poppins text-3xl sm:text-5xl font-semibold tracking-tight text-foreground">
              Application Received
            </h1>
            <p className="mt-4 text-base sm:text-lg text-muted-foreground">
              You\u2019re one step closer to becoming an expert on Soulversity.
            </p>
            <p className="mt-2 text-sm sm:text-base text-ifind-purple font-medium">
              Your application is now under review.
            </p>
          </div>

          {/* What happens next */}
          <div className="mt-16 w-full max-w-5xl mx-auto flex flex-col items-center">
            <div className="flex flex-col items-center text-center max-w-2xl mx-auto">
              <h2 className="font-poppins text-2xl sm:text-3xl font-semibold text-foreground">
                What happens next
              </h2>
              <p className="mt-2 text-sm sm:text-base text-muted-foreground">
                A simple, transparent journey designed for depth and quality.
              </p>
            </div>

            <ol className="mt-8 sm:mt-10 w-full grid gap-5 sm:gap-6 md:grid-cols-3">
              {steps.map((s, i) => {
                const Icon = s.icon;
                return (
                  <li
                    key={s.title}
                    className="rounded-2xl border border-border bg-card p-6 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1 animate-fade-in flex flex-col items-center text-center"
                    style={{ animationDelay: `${i * 100}ms` }}
                  >
                    <div className="h-11 w-11 rounded-xl bg-ifind-aqua/10 border border-border flex items-center justify-center text-ifind-purple">
                      <Icon className="h-5 w-5" />
                    </div>
                    <span className="mt-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Step {i + 1}
                    </span>
                    <h3 className="mt-2 font-poppins text-lg font-semibold text-foreground">
                      {s.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                      {s.desc}
                    </p>
                  </li>
                );
              })}
            </ol>
          </div>

          {/* Post-approval unlocks */}
          <div className="mt-16 w-full max-w-3xl mx-auto">
            <div className="rounded-2xl border border-border bg-ifind-offwhite p-6 sm:p-8 flex flex-col items-center text-center">
              <h3 className="font-poppins text-lg sm:text-xl font-semibold text-foreground">
                What You Unlock After Approval
              </h3>
              <ul className="mt-5 w-full grid gap-3 sm:grid-cols-3">
                {unlocks.map((u) => {
                  const Icon = u.icon;
                  return (
                    <li
                      key={u.label}
                      className="flex flex-col items-center text-center gap-3 rounded-xl bg-background border border-border p-4"
                    >
                      <div className="h-9 w-9 rounded-lg bg-ifind-teal/15 text-ifind-teal flex items-center justify-center">
                        <Icon className="h-4 w-4" />
                      </div>
                      <span className="text-sm text-foreground">{u.label}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          {/* Trust / mission */}
          <div className="mt-12 max-w-2xl mx-auto flex flex-col items-center text-center">
            <p className="text-base sm:text-lg leading-relaxed text-foreground/80 italic">
              &ldquo;Every expert on Soulversity is carefully selected to ensure
              depth, consistency, and meaningful transformation for users
              worldwide.&rdquo;
            </p>
          </div>

          {/* WhatsApp group invite */}
          <div className="mt-12 w-full max-w-lg mx-auto">
            <div className="rounded-2xl border border-ifind-teal/20 bg-ifind-teal/5 p-6 sm:p-8 flex flex-col items-center text-center">
              <h3 className="font-poppins text-xl sm:text-2xl font-semibold text-foreground">
                💬 While You Wait
              </h3>
              <p className="mt-3 text-sm sm:text-base text-muted-foreground leading-relaxed">
                Please join the Soulversity Expert WhatsApp group by clicking on this link.
                All further communication will be held there.
              </p>
              <a
                href="https://chat.whatsapp.com/I51rZtc6BPS7Cnmu1ur0eR"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 inline-flex items-center gap-2 rounded-xl bg-[#25D366] px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-[#128C7E] transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Join Our WhatsApp Group
              </a>
            </div>
          </div>

          {/* CTAs */}
          <div className="mt-10 w-full max-w-sm mx-auto flex flex-col items-center gap-3">
            <Button
              size="lg"
              className="w-full bg-ifind-purple hover:bg-ifind-purple/90 text-white"
              onClick={() => navigate('/')}
            >
              Return to Home
              <ArrowRight className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              className="w-full text-muted-foreground hover:text-ifind-purple"
              onClick={() => navigate('/contact')}
            >
              <LifeBuoy className="h-4 w-4" />
              Contact Support
            </Button>
          </div>

          {/* Footer note */}
          <div className="mt-8 max-w-xl mx-auto flex flex-col items-center text-center">
            <p className="text-xs sm:text-sm text-muted-foreground">
              Shortlisted applicants are typically contacted within 2–3 days.
              No further action is needed at this stage.
            </p>
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ExpertApplicationSubmitted;
